import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Visit } from '@/types/visit.types';
import { visitsService } from '@/services/visits.service';
import { VisitStatusBadge } from '@/components/visits/VisitStatusBadge';

interface UpcomingVisitsProps {
  visits?: Visit[];
  isLoading?: boolean;
  limit?: number;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  }
  if (date.toDateString() === tomorrow.toDateString()) {
    return 'Tomorrow';
  }

  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
};

const formatTime = (timeString?: string): string => {
  if (!timeString) return '';
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center gap-4 animate-pulse">
        <div className="w-14 h-14 rounded-lg bg-secondary-100"></div>
        <div className="flex-1">
          <div className="h-4 bg-secondary-200 rounded w-2/3 mb-2"></div>
          <div className="h-3 bg-secondary-100 rounded w-1/3"></div>
        </div>
        <div className="h-6 bg-secondary-100 rounded w-20"></div>
      </div>
    ))}
  </div>
);

const VisitItem: React.FC<{ visit: Visit; onClick: () => void }> = ({ visit, onClick }) => {
  const dateStr = formatDate(visit.scheduled_date);
  const timeStr = formatTime(visit.scheduled_time);
  const isToday = dateStr === 'Today';
  const isTomorrow = dateStr === 'Tomorrow';

  return (
    <div
      onClick={onClick}
      className="flex items-center gap-4 p-3 rounded-lg hover:bg-secondary-50 cursor-pointer transition-colors -mx-3"
    >
      {/* Date Box */}
      <div
        className={`flex flex-col items-center justify-center w-14 h-14 rounded-lg ${
          isToday
            ? 'bg-primary-100 text-primary-700'
            : isTomorrow
            ? 'bg-amber-100 text-amber-700'
            : 'bg-secondary-100 text-secondary-700'
        }`}
      >
        {isToday || isTomorrow ? (
          <>
            <span className="text-xs font-medium">{dateStr}</span>
            {timeStr && <span className="text-xs">{timeStr}</span>}
          </>
        ) : (
          <>
            <span className="text-xs font-medium">
              {new Date(visit.scheduled_date).toLocaleDateString('en-IN', { weekday: 'short' })}
            </span>
            <span className="text-lg font-bold">
              {new Date(visit.scheduled_date).getDate()}
            </span>
          </>
        )}
      </div>

      {/* Visit Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-secondary-900 truncate">{visit.lead.name}</p>
        <div className="flex items-center gap-2 mt-1">
          {visit.sales_kit && (
            <span className="text-xs text-secondary-500 truncate">
              {visit.sales_kit.name}
            </span>
          )}
          {!isToday && !isTomorrow && timeStr && (
            <span className="text-xs text-secondary-400">{timeStr}</span>
          )}
        </div>
      </div>

      {/* Status */}
      <VisitStatusBadge status={visit.status} size="sm" />
    </div>
  );
};

export const UpcomingVisits: React.FC<UpcomingVisitsProps> = ({
  visits: propVisits,
  isLoading: propIsLoading,
  limit = 5,
}) => {
  const navigate = useNavigate();
  const [visits, setVisits] = useState<Visit[]>(propVisits ?? []);
  const [isLoading, setIsLoading] = useState(propIsLoading ?? true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (propVisits !== undefined) {
      setVisits(propVisits);
    }
  }, [propVisits]);

  useEffect(() => {
    if (propIsLoading !== undefined) {
      setIsLoading(propIsLoading);
    }
  }, [propIsLoading]);

  useEffect(() => {
    const fetchUpcomingVisits = async () => {
      // Skip if visits provided via props
      if (propVisits !== undefined) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await visitsService.getUpcoming(7);
        // API interceptor unwraps { success: true, data: {...} } to just the data
        // Backend may return array directly or { visits: [...] }
        const visitsData = Array.isArray(response.data)
          ? response.data
          : (response.data?.visits || []);
        setVisits(visitsData.slice(0, limit));
      } catch (err) {
        setError('Failed to load upcoming visits');
        console.error('Error fetching upcoming visits:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUpcomingVisits();
  }, [propVisits, limit]);

  return (
    <Card className="h-full" padding="none">
      {/* Header */}
      <div className="px-6 py-4 border-b border-secondary-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-secondary-900">Upcoming Visits</h3>
          <button
            onClick={() => navigate('/visits')}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            View All
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {isLoading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-sm text-secondary-500">{error}</p>
          </div>
        ) : visits.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary-100 mb-3">
              <svg
                className="w-6 h-6 text-secondary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <p className="text-sm text-secondary-500 mb-3">No upcoming visits</p>
            <button
              onClick={() => navigate('/visits/schedule')}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Schedule a Visit
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            {visits.map((visit) => (
              <VisitItem
                key={visit.id}
                visit={visit}
                onClick={() => navigate(`/visits/${visit.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer - Quick Schedule */}
      {!isLoading && visits.length > 0 && (
        <div className="px-6 pb-4">
          <button
            onClick={() => navigate('/visits/schedule')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-secondary-200 rounded-lg text-sm font-medium text-secondary-600 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Schedule New Visit
          </button>
        </div>
      )}
    </Card>
  );
};

export default UpcomingVisits;
